// ═══════════════════════════════════════════════════════════════════════════
// PAGE: ÉDITION / CRÉATION D'UNE RÉUNION (RefactoUX/Réunions — mai 2026)
// ═══════════════════════════════════════════════════════════════════════════
//
// Route : `#meeting/:id` — id=0 = création, id>0 = édition.
// Persistance : champs `program_id` + `with_risks` ajoutés à la table
// `meetings` le 2026-05-21.
//
// Structure :
//   - Bloc "Informations" toujours visible (libellé, programme, projet,
//     comité, date, heure, ✓ Inclure le traitement des risques).
//   - En CRÉATION : bloc "Animateurs et participants" en-dessous.
//   - En ÉDITION + animateur de la réunion : système d'onglets style
//     #matrix/:id sous le bloc Informations :
//        · Animateurs et Participants (= bloc actuel + propositions)
//        · Principales Actions (liste + CTA "Mettre à jour la liste")
//        · Notes de réunions (placeholder)
//        · Génération CR (placeholder)
//        · Génération Actions (placeholder)
//   - En ÉDITION + non-animateur : bloc Informations seul (pas d'onglets).
//
// Onglet actif persistant via le query string `?tab=<key>` du hash.

import { definePage, navigateTo } from '../router.js';
import type { InvitationResult } from './invitation-proposal.js';
import {
  readTabFromHash,
  computeVisibleTabs,
  resolveActiveTab,
  getVisibleProjects,
  sanitizeMeetingPrefill,
  type TabKey,
  type MeetingCreatePrefill,
  type MeetingPageState,
} from '../domain/meeting-form.js';
import { meetingContextFrom } from '../domain/meeting-guide-action.js';
import { deriveMeetingRoles, deriveMeetingCapabilities } from '../domain/meeting-capabilities.js';
import {
  MASCOT_CASE_MEETING_OPEN,
  applyInviteeHighlights,
  createRaciSync,
  proposeParticipants,
  wireRaciInScope,
} from './page-meeting-invitees.js';
import {
  linkActionsHash,
  loadActionsTab,
  loadIndicatorsTab,
  rememberSupportBackHash,
  supportWizardHash,
} from './page-meeting-actions-tab.js';
import { saveMeeting } from './page-meeting-save.js';
import { wireSteps } from './page-meeting-steps.js';
import { tabContentHtml } from './page-meeting-tab-views.js';
import { wireGuidesHeader } from './page-meeting-guides.js';
import { meetingHeaderHtml, wireMeetingHeader } from './page-meeting-header.js';
import {
  applyMeetingLocks,
  createTabSwitcher,
  tabLabel,
  tabsBarHtml,
  wireTabsBar,
} from './page-meeting-tabs.js';
import { createDraftActionsTable } from './page-meeting-draft-actions.js';
import { createGenerationTab } from './page-meeting-generation.js';
import { createCrTab } from './page-meeting-cr.js';
import { createNotesTab } from './page-meeting-notes.js';

interface MeetingUser {
  id: number;
  trigram: string;
  name: string;
}

interface MeetingRecord {
  id: number;
  project_id: number | null;
  project_code: string | null;
  project_name: string | null;
  program_id: number | null;
  committee_id: number;
  committee_code: string | null;
  committee_name: string | null;
  structure_id: number | null;
  structure_code: string | null;
  structure_label: string | null;
  meeting_date: string | null;
  meeting_time: string | null;
  label: string;
  with_risks: boolean;
  created_by: number | null;
  opened_at: string | null;
  closed_at: string | null;
  participants: MeetingUser[];
  animators: MeetingUser[];
  cr_count: number;
  notes_count: number;
  // Réunion issue d'un calendrier utilisateur (ical_uid présent) : garde de déplacement à l'enregistrement.
  from_calendar?: boolean;
  // Occurrence d'une série récurrente : propose la propagation comité/projet/
  // programme/structure aux occurrences suivantes à l'enregistrement.
  recurring?: boolean;
}

interface UserItem {
  id: number;
  trigram: string;
  name: string;
  is_team: boolean;
}

interface CommitteeItem {
  id: number;
  code: string;
  name: string;
}

interface ProjectItem {
  id: number;
  code: string;
  name: string;
}

interface ProgramItem {
  id: number;
  code?: string | null;
  name: string;
  projects?: Array<{ id: number }>;
}

interface StructureItem {
  id: number;
  code: string;
  label: string;
  is_active?: boolean;
}

// Onglets (TAB_ORDER), lecture URL, visibilité, validation et payload sont
// désormais dans domain/meeting-form (pur + testé).

// Pré-remplissage one-shot du formulaire de création, posé par la vue
// calendrier des réunions (Dupliquer / Ajouter). Lu une fois puis effacé.
// Clé partagée avec page-meetings-records.ts (MEETING_CREATE_PREFILL_KEY).
const MEETING_CREATE_PREFILL_KEY = 'meeting:createPrefill';
function _consumeCreatePrefill(): MeetingCreatePrefill | null {
  try {
    const raw = sessionStorage.getItem(MEETING_CREATE_PREFILL_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(MEETING_CREATE_PREFILL_KEY);
    return JSON.parse(raw) as MeetingCreatePrefill;
  } catch {
    return null;
  }
}

pages.meeting = definePage<'meeting/:id'>(async ({ id }) => {
  const isNew = id === 0;
  const canWrite = !!(window.hasPermission && window.hasPermission('meetings_records.write'));

  setHeader(esc(t(isNew ? 'pageMeeting.titleCreate' : 'pageMeeting.titleEdit')), '');
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);

  if (isNew && !canWrite) {
    setContent(`<div class="error">${esc(t('common.accessDenied'))}</div>`);
    return;
  }

  let meeting: MeetingRecord | null = null;
  let users: UserItem[];
  let committees: CommitteeItem[];
  let projects: ProjectItem[];
  let programs: ProgramItem[];
  let structures: StructureItem[];

  try {
    const [single, allUsers, allCommittees, allProjects, allPrograms, allStructures] =
      (await Promise.all([
        // Réunion seule (GET /meetings-records/:id). En création : pas de fetch.
        // Un échec (404 / réseau) → null → message "introuvable" ci-dessous.
        isNew
          ? Promise.resolve(null)
          : (api.getMeetingRecord(id) as Promise<MeetingRecord>).catch(() => null),
        api.getUsers(),
        api.getCommittees(),
        api.getProjects(),
        api.getPrograms(),
        (api.getStructuresIfAllowed() as Promise<StructureItem[]>).catch(() => []),
      ])) as [
        MeetingRecord | null,
        UserItem[],
        CommitteeItem[],
        ProjectItem[],
        ProgramItem[],
        StructureItem[],
      ];
    users = allUsers.filter((u) => !u.is_team);
    committees = allCommittees;
    projects = allProjects;
    programs = allPrograms;
    structures = (allStructures || []).filter((s) => s.is_active !== false);
    if (!isNew) {
      meeting = single;
      if (!meeting) {
        setHeader(esc(t('pageMeeting.titleEdit')), '');
        setContent(
          `<div class="error" style="padding:20px">
            <p>${esc(t('pageMeeting.notFound').replace('{id}', String(id)))}</p>
            <p style="margin-top:12px"><a href="#meetings-records" class="btn btn-secondary">← ${esc(t('pageMeetingsRecords.title'))}</a></p>
          </div>`,
        );
        return;
      }
    }
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : t('common.error');
    setContent(`<div class="error">${esc(msg)}</div>`);
    return;
  }

  // Rôles utilisateur sur la réunion — pilote la visibilité des onglets.
  // - Animateur : tous les onglets.
  // - Participant (non animateur) : uniquement l'onglet Notes.
  // - Aucun rôle : pas d'onglets, juste le bloc Informations en lecture.
  const uid = currentUserId();
  const { isAnimator, isParticipant } = deriveMeetingRoles(meeting, uid);

  renderMeetingPage(
    meeting,
    users,
    committees,
    projects,
    programs,
    structures,
    canWrite,
    isAnimator,
    isParticipant,
  );
});

function renderMeetingPage(
  meeting: MeetingRecord | null,
  users: UserItem[],
  committees: CommitteeItem[],
  projects: ProjectItem[],
  programs: ProgramItem[],
  structures: StructureItem[],
  canWrite: boolean,
  isAnimator: boolean,
  isParticipant: boolean,
): void {
  // Cycle de vie de la réunion (cf. backend `meetings.opened_at` / `closed_at`) :
  // - opened_at NULL → préparation, seuls les animateurs voient les onglets
  //   sensibles (notes, cr, generation). Les participants n'ont rien.
  // - opened_at set, closed_at NULL → ouverte, participants accèdent à Notes.
  // - closed_at set → fermée, tout en lecture seule.
  const isOpen = !!meeting?.opened_at;
  const isClosed = !!meeting?.closed_at;
  // Capacités d'écran (rôle × cycle de vie × palier × solo) : dérivation pure
  // centralisée dans domain/meeting-capabilities.ts (testée sans DOM).
  const caps = deriveMeetingCapabilities({
    hasMeeting: !!meeting,
    isAnimator,
    isParticipant,
    isOpen,
    isClosed,
    canWrite,
    isSolo: !!window.isSoloUser?.(),
    can: (p) => !!(window.hasPermission && window.hasPermission(p)),
  });
  // Label dynamique du CTA bas-droite : pour un pur participant en
  // consultation (bloc Information non éditable) tant que la réunion n'est
  // pas ouverte, on remplace "Annuler" par "Fermer" — "Annuler" sous-entend
  // qu'on abandonne une saisie, alors qu'ici on quitte simplement la page.
  function _cancelLabel(): string {
    return t(caps.cancelLabelKey);
  }
  // Onglets visibles selon le rôle (animator > participant > rien).
  // En création (pas de meeting), on affiche tous les onglets : le créateur
  // est animateur par défaut. Les onglets qui nécessitent une réunion
  // enregistrée (actions, notes, support, cr, generation) affichent un
  // placeholder « Enregistrer d'abord » tant que la réunion n'a pas d'id.
  const visibleTabs: TabKey[] = computeVisibleTabs({
    hasMeeting: !!meeting,
    isAnimator,
    isParticipant,
    isOpen,
  });
  const showTabs = visibleTabs.length > 0;
  // En création, l'onglet "Participants" prend en charge le bloc équivalent —
  // on ne rend plus le bloc séparé.
  const showParticipantsBlock = false;
  // En édition, le bloc Informations est en lecture seule pour les non-
  // animateurs (participant ou observateur) — cf. caps.infoReadOnly /
  // caps.infoDisabled (seuls les animateurs modifient l'entête).
  // Création : nécessite le droit global meetings_records.write. Édition d'une
  // réunion existante : un animateur suffit (cohérent avec le backend, qui
  // autorise l'animateur — utile pour rattacher un brouillon issu du calendrier
  // à un comité même sans le droit global d'écriture).
  const canSaveMeeting = caps.canSaveMeeting;

  // ── State local ─────────────────────────────────────────────────────────
  // En création, l'utilisateur courant est positionné par défaut comme
  // animateur — il accédera ainsi à tous les onglets après enregistrement.
  const currentUid = currentUserId();
  const state: MeetingPageState = {
    programId: meeting?.program_id ?? 0,
    projectId: meeting?.project_id ?? 0,
    structureId: meeting?.structure_id ?? 0,
    committeeId: meeting?.committee_id ?? 0,
    meetingDate: meeting?.meeting_date ?? '',
    meetingTime: meeting?.meeting_time ?? '',
    label: meeting?.label ?? '',
    withRisks: !!meeting?.with_risks,
    participantIds: meeting ? meeting.participants.map((p) => p.id) : [],
    animatorIds: meeting ? meeting.animators.map((a) => a.id) : currentUid ? [currentUid] : [],
    activeTab: readTabFromHash(window.location.hash) as TabKey,
  };

  // Si l'onglet courant (depuis l'URL) n'est pas visible pour ce rôle, on
  // retombe sur le 1er onglet visible.
  state.activeTab = resolveActiveTab(state.activeTab, visibleTabs);

  // En création (meeting === null), applique un éventuel pré-remplissage posé
  // par la vue calendrier (Dupliquer copie l'entête + RACI ; Ajouter la date).
  if (!meeting) {
    const pf = _consumeCreatePrefill();
    if (pf) Object.assign(state, sanitizeMeetingPrefill(pf));
  }

  function visibleProjects(): ReadonlyArray<ProjectItem> {
    return getVisibleProjects(state.programId, programs, projects);
  }

  // Dernière proposition de participants calculée : elle sert à relire la
  // liste déjà saisie (soulignement par score), pas seulement à remplir le
  // panneau. Remise à null au changement d'onglet — le calcul dépend du
  // périmètre affiché, une mise en évidence orpheline mentirait.
  // ⚠️ Déclarée ICI, en tête : `wireActiveTab()` la lit et s'exécute bien
  // avant le bloc « Proposer une liste » plus bas dans cette (longue)
  // fonction — un `let` déclaré là-bas serait en zone morte temporelle.
  let lastProposal: InvitationResult | null = null;

  // Table « Nouvelles actions » de l'onglet Notes (page-meeting-draft-actions).
  // Absente en création : la réunion n'a pas encore d'id à qui rattacher.
  const draftTable = meeting
    ? createDraftActionsTable({ meetingId: meeting.id, users, isClosed })
    : null;
  // Onglet « Générer les actions » (page-meeting-generation). Les
  // rattachements sont lus au clic : les selects de l'entête peuvent avoir
  // changé depuis le rendu de l'onglet.
  const generationTab = meeting
    ? createGenerationTab({
        meetingId: meeting.id,
        users,
        projects,
        isClosed,
        getLinks: () => ({
          projectId: state.projectId,
          committeeId: state.committeeId,
          structureId: state.structureId,
        }),
      })
    : null;
  // Onglet « Génération CR » (page-meeting-cr). Après fermeture, la page se
  // re-rend via le router pour passer en lecture seule.
  const crTab = meeting
    ? createCrTab({
        meetingId: meeting.id,
        canAnalyze: caps.canAnalyze,
        onClosed: () => {
          if (typeof router?.dispatch === 'function') router.dispatch();
        },
      })
    : null;

  function refreshInviteeHighlights(): void {
    applyInviteeHighlights(lastProposal);
  }

  // Entête, barre d'onglets et contenu des onglets : page-meeting-header.ts,
  // page-meeting-tabs.ts, page-meeting-tab-views.ts (extraction 2026-09-08,
  // point 16). Le contexte est relu à chaque rendu : l'état change.
  function renderTabContent(): string {
    return tabContentHtml({
      meeting,
      state,
      users,
      projects,
      committees,
      structures,
      isAnimator,
      isOpen,
      isClosed,
      showCloseBar: caps.showCloseBar,
      infoDisabled: caps.infoDisabled,
    });
  }

  // ── HTML principal ──────────────────────────────────────────────────────
  const headerHtml = meetingHeaderHtml({
    meeting,
    state,
    programs,
    committees,
    structures,
    visibleProjects,
    infoDisabled: caps.infoDisabled,
    tabLabel,
  });

  // Bloc participants : en création (sans onglets) ou en édition+animateur
  // (dans l'onglet "participants" via renderTabContent).
  const middleHtml = showTabs
    ? `${tabsBarHtml(visibleTabs, state.activeTab)}<div id="mp-tab-content">${renderTabContent()}</div>`
    : showParticipantsBlock
      ? renderTabContent()
      : '';

  const html = `
    ${headerHtml}
    <div style="display:flex;flex-direction:column;gap:14px;max-width:1600px">
      ${middleHtml}
      <div class="mp-bottom-bar" style="position:sticky;bottom:0;background:var(--bg);border-top:1px solid var(--border);padding:12px 0;display:flex;justify-content:flex-end;gap:8px;z-index:5">
        <button type="button" id="mp-cancel" class="btn btn-secondary">${esc(_cancelLabel())}</button>
        ${canSaveMeeting ? `<button type="button" id="mp-save" class="btn btn-primary">${esc(t('common.save'))}</button>` : ''}
      </div>
    </div>`;

  setContent(html);

  // Verrous de palier (Solo, Indicateurs) : page-meeting-tabs.ts.
  applyMeetingLocks(caps);

  // ── Câblages ────────────────────────────────────────────────────────────

  // Bloc Informations (cascade programme → projet, champs, volet repliable) :
  // page-meeting-header.ts. Les champs RACI de l'onglet Participants sont
  // câblés à chaque rendu de l'onglet (wireRaciInScope, page-meeting-invitees).
  wireMeetingHeader(state, visibleProjects);

  document.getElementById('mp-cancel')?.addEventListener('click', () => {
    navigateTo('meetings-records', {});
  });
  document.getElementById('mp-save')?.addEventListener('click', () => {
    void saveMeeting(meeting, state);
  });

  // Onglet Notes (page-meeting-notes) : porte le drapeau « saisie non
  // enregistrée » (garde anti-perte, audit UX 2026-06) et le polling de la
  // table des brouillons. Le garde global (nav + beforeunload) et le switch
  // d'onglet le lisent ; la page seule décide de la confirmation d'abandon.
  const notesTab = meeting
    ? createNotesTab({
        meetingId: meeting.id,
        draftTable,
        onOpened: () => {
          // Reload du handler via dispatch du router (préserve l'URL).
          if (typeof router?.dispatch === 'function') router.dispatch();
        },
      })
    : null;
  setUnsavedGuard(() => notesTab?.isDirty() ?? false);
  // Arrête le polling à la navigation (changement de hash route).
  const _onHashChange = (): void => notesTab?.stopPolling();
  window.addEventListener('hashchange', _onHashChange);
  // Sans ce disposer, chaque fiche réunion ouverte laissait un listener de
  // plus, retenant son onglet Notes et sa table de brouillons.
  registerPageDisposer(() => window.removeEventListener('hashchange', _onHashChange));

  // Recopie des pastilles RACI dans `state` tant que l'onglet Participants
  // est monté : une seule observation à la fois (page-meeting-invitees).
  const raciSync = createRaciSync(state, refreshInviteeHighlights);
  registerPageDisposer(raciSync.stop);

  // Bascule d'onglet (page-meeting-tabs) : hash, styles, garde « notes non
  // enregistrées ». La page fournit le contenu et le câblage de l'onglet.
  const { requestSwitchTab } = createTabSwitcher({
    state,
    meetingId: meeting?.id ?? null,
    isDirty: () => notesTab?.isDirty() ?? false,
    markClean: () => notesTab?.markClean(),
    onLeave: () => {
      notesTab?.stopPolling();
      raciSync.stop();
    },
    renderContent: renderTabContent,
    wireContent: () => wireActiveTab(),
  });

  // (Re)câble les listeners propres au contenu de l'onglet actif. Appelé au
  // boot et à chaque switchTab. Sépare le câblage du HTML pour pouvoir
  // re-render uniquement la zone tab-content sans dupliquer les listeners
  // sur l'info-block ou le sticky-bottom.
  function wireActiveTab(): void {
    const host = document.getElementById('mp-tab-content');
    if (!host) return;
    if (state.activeTab === 'participants') {
      wireRaciInScope(host, users, canWrite);
      host.querySelector<HTMLButtonElement>('#mp-propose-btn')?.addEventListener('click', () => {
        void onPropose();
      });
      // Le panneau de proposition est re-rendu à chaque passage sur l'onglet :
      // la mise en évidence précédente ne survit pas, on repart d'un calcul
      // neuf (et le compteur, lui, s'affiche tout de suite).
      lastProposal = null;
      refreshInviteeHighlights();
      raciSync.restart();
    } else if (state.activeTab === 'actions' && meeting) {
      const meetingId = meeting.id;
      // Mode rattachement : #actions prend la main et revient ici via PUT.
      host
        .querySelector<HTMLButtonElement>('#mp-actions-update-btn')
        ?.addEventListener('click', () => {
          window.location.hash = linkActionsHash(meetingId, state);
        });
      void loadActionsTab(meetingId, users, projects);
    } else if (state.activeTab === 'support' && meeting) {
      const meetingId = meeting.id;
      host.querySelector<HTMLButtonElement>('#mp-support-launch')?.addEventListener('click', () => {
        rememberSupportBackHash(meetingId);
        window.location.hash = supportWizardHash(state);
      });
    } else if (state.activeTab === 'notes') {
      notesTab?.wire(host);
    } else if (state.activeTab === 'cr') {
      crTab?.wire(host);
    } else if (state.activeTab === 'generation') {
      if (generationTab) {
        void generationTab.load();
        generationTab.wire(host);
      }
    } else if (state.activeTab === 'indicators' && meeting) {
      void loadIndicatorsTab(meeting.id, caps.indicatorsAllowed);
    }
  }

  // Câblage initial selon le rendu courant (avec ou sans onglets).
  if (showTabs) {
    wireTabsBar(requestSwitchTab);
    wireActiveTab();
  } else if (showParticipantsBlock) {
    wireRaciInScope(document, users, canWrite);
    document.getElementById('mp-propose-btn')?.addEventListener('click', () => {
      void onPropose();
    });
  }
  // Le guide (numéros cliquables) est rendu dans le bloc Informations, présent
  // quel que soit l'état des onglets — câblage indépendant.
  wireSteps(visibleTabs, requestSwitchTab);
  // Conseil d'animation du Professeur Ornaxion : à l'ouverture d'une fiche
  // EXISTANTE seulement — en création, l'animateur saisit, il ne prépare pas
  // encore. Les gardes (mobile, préférence, cooldown, fréquence) sont dans le
  // composant.
  if (meeting) void window.OrnaxionMascot?.onPageOpened(MASCOT_CASE_MEETING_OPEN);
  // 3e colonne du bandeau : récapitulatif des guides, chargé à part. Sans
  // réunion enregistrée il n'y a pas encore de snapshot à montrer.
  if (meeting) {
    // Créer une action depuis un guide : animateur ET droit d'écriture sur
    // les actions, sinon le CTA est masqué (cf. meetingContextFrom).
    const canCreateAction = isAnimator && !!window.hasPermission?.('actions.write');
    wireGuidesHeader(
      meeting.id,
      isAnimator,
      caps.infoDisabled,
      meetingContextFrom(state, currentUid, canCreateAction),
    );
  }

  // Chargements des onglets Actions et Indicateurs, liens vers #actions et le
  // wizard support : page-meeting-actions-tab.ts. Enregistrement :
  // page-meeting-save.ts. Proposition de participants : page-meeting-invitees
  // (la page ne garde que la mise en évidence qui en découle).
  async function onPropose(): Promise<void> {
    const result = await proposeParticipants(state, users);
    if (!result) return;
    lastProposal = result;
    refreshInviteeHighlights();
  }
}

export {};
